import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../components/contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import Breadcrumb from '../components/common/Breadcrumb';
import ROUTES from '../constants/routes';
import EntityTable from '../components/common/EntityTable';
import EntityModal from '../components/common/EntityModal';
import FilterPanel from '../components/common/FilterPanel';
import Pagination from '../components/common/Pagination';
import ConfirmDialog from '../components/common/ConfirmDialog';
import usePaginatedEntity from '../hooks/usePaginatedEntity';
import { useToast } from '../components/contexts/ToastContext';
import apiService from '../services/apiService';
import { isOrdenTrabajoVencida, getDateKey } from '../utils/dateHelpers';
import { validateDate } from '../utils/sanitization';
import { downloadFile } from '../utils/downloadHelpers';
import { useExportExcel } from '../hooks/useExportExcel';
import HighlightedText from '../components/common/HighlightedText';
import { renderActionButton } from '../components/common/EntityTable';
import { renderEquipoCell } from '../utils/renderHelpers';
import { prioridadBadge, dotColors, getOTBaseColumns, createOTInformeHandlers } from '../utils/otRenderHelpers';
import HistorialProgramacionModal from '../components/ordenesTrabajo/HistorialProgramacionModal';
import GenerarInformeModal from '../components/ordenesTrabajo/GenerarInformeModal';

/**
 * Página de gestión de órdenes de trabajo de equipos
 *
 * Permite crear, editar, eliminar y filtrar órdenes de trabajo por estado,
 * tipo de mantenimiento, prioridad y programa vinculado.
 *
 * @returns {JSX.Element}
 */
function OrdenesTrabajo() {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();

  // Leer programa_mantenimiento_id desde URL params (para navegacion desde Programas)
  const searchParams = new URLSearchParams(window.location.search);
  const programaIdFromUrl = searchParams.get('programa_mantenimiento_id') || '';

  // Hook CRUD principal
  const {
    data: ordenesTrabajo,
    rawData,
    loading,
    pagination,
    filters,
    modalOpen,
    editingItem: editingOrdenTrabajo,
    confirmDialog,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    handleCreate,
    handleEdit,
    handleDelete,
    confirmDelete,
    handleSave,
    closeModal,
    closeConfirm,
    refetch,
    sortField,
    sortOrder,
    handleSort
  } = usePaginatedEntity({
    entityName: 'ordenTrabajo',
    entityNamePlural: 'órdenes de trabajo',
    fetchFn: apiService.getOrdenesTrabajo,
    createFn: apiService.createOrdenTrabajo,
    updateFn: apiService.updateOrdenTrabajo,
    deleteFn: apiService.deleteOrdenTrabajo,
    defaultFilters: {
      search: '',
      programa_mantenimiento_id: programaIdFromUrl
    },
    filterKeys: ['search', 'estado', 'prioridad', 'tipo_mantenimiento_id', 'programa_mantenimiento_id'],
    messages: {
      createSuccess: 'Orden de trabajo creada',
      updateSuccess: 'Orden de trabajo actualizada',
      deleteSuccess: 'Orden de trabajo eliminada exitosamente'
    }
  });

  // Lógica específica: Carga de catálogos
  const [equipos, setEquipos] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [plantillasList, setPlantillasList] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [motivosReprogramacion, setMotivosReprogramacion] = useState([]);

  // Programar dialog
  const [programarDialog, setProgramarDialog] = useState({ open: false, item: null });

  // Historial programación
  const [historialDialog, setHistorialDialog] = useState({ open: false, data: [], item: null });

  // Informe de OT
  const [informeDialog, setInformeDialog] = useState({ open: false, item: null });
  const [generandoInforme, setGenerandoInforme] = useState(false);

  // Handlers compartidos (historial + informes)
  const { handleVerHistorial, handleGenerarInforme, handleConfirmarInforme } = createOTInformeHandlers({
    apiService,
    showToast,
    downloadFile,
    setHistorialDialog,
    setInformeDialog,
    setGenerandoInforme,
    getInformeItem: () => informeDialog.item,
    onInformeSuccess: refetch
  });

  /**
   * Carga catálogos de equipos, tipos de mantenimiento y plantillas
   * @returns {Promise<void>}
   */
  const fetchCatalogos = useCallback(async () => {
    try {
      const [equiposRes, tiposRes, plantillasRes, usuariosRes, motivosRes] = await Promise.all([
        apiService.getEquipos({ limit: 1000 }),
        apiService.getTiposOrdenTrabajo(),
        apiService.getPlantillasMantenimientoLista(),
        apiService.getUsuariosLista(),
        apiService.getMotivosReprogramacionLista()
      ]);
      if (equiposRes.data) setEquipos(equiposRes.data);
      if (tiposRes.success) setTipos(tiposRes.data);
      if (plantillasRes.success) setPlantillasList(plantillasRes.data || []);
      if (usuariosRes.success) setUsuarios(usuariosRes.data || []);
      if (motivosRes.success) setMotivosReprogramacion(motivosRes.data || []);
    } catch (error) {
      console.error('Error fetching catalogos:', error);
    }
  }, []);

  useEffect(() => {
    fetchCatalogos();
  }, [fetchCatalogos]);

  /**
   * Maneja programación/reprogramación de orden de trabajo
   * @param {Object} formData - { fecha_ejecucion, usuario_asignado_id, motivo }
   * @returns {Promise<void>}
   */
  const handleProgramar = useCallback(async (formData) => {
    if (!programarDialog.item) return;
    try {
      const response = await apiService.programarOrdenTrabajo(programarDialog.item.id, formData);
      if (response.success) {
        showToast('Orden de trabajo programada exitosamente', 'success');
        setProgramarDialog({ open: false, item: null });
        refetch();
      } else {
        showToast(response.message || 'Error al programar', 'error');
      }
    } catch (error) {
      showToast('Error al programar orden de trabajo', 'error');
    }
  }, [programarDialog.item, showToast, refetch]);

  /**
   * Exporta órdenes de trabajo visibles a archivo Excel
   * @returns {void}
   */
  const exportToExcel = useExportExcel({
    data: rawData,
    mapFn: (ot) => ({
      'Código OT': ot.codigo_ot || '',
      'Fecha Ejec.': ot.fecha_ejecucion ? new Date(ot.fecha_ejecucion).toLocaleDateString('es-CL') : '',
      'Equipo': ot.equipo ? `${ot.equipo.codigo} - ${ot.equipo.marca} ${ot.equipo.modelo}` : '',
      'Plantilla': ot.plantilla?.nombre || '',
      'Prioridad': ot.prioridad || '',
      'Estado': ot.estado?.replace('_', ' ') || '',
      'Asignado': ot.usuario_asignado?.nombre || ''
    }),
    sheetName: 'OrdenesTrabajo',
    filePrefix: 'ordenes_trabajo_',
    columnWidths: [{ wch: 14 }, { wch: 12 }, { wch: 30 }, { wch: 20 }, { wch: 10 }, { wch: 14 }, { wch: 25 }],
    entityLabel: 'órdenes de trabajo'
  });

  const columns = useMemo(() => getOTBaseColumns({ includeAsignado: true }), []);

  const otActions = [
    { icon: 'fa-history',        className: 'btn-ghost-warning', title: 'Ver historial de programación',   onClick: handleVerHistorial,                                                 show: () => hasPermission('ordenes_trabajo.read'),   disabled: (row) => row.programaciones_count === 0 },
    { icon: 'fa-calendar-plus',  className: 'btn-ghost-warning', title: 'Programar',                      onClick: (row) => setProgramarDialog({ open: true, item: row }),             show: () => hasPermission('ordenes_trabajo.schedule'), disabled: (row) => row.estado !== 'programado' },
    { icon: 'fa-file-signature', className: 'btn-ghost-success', title: 'Generar informe firmado',        onClick: handleGenerarInforme, show: (row) => !row.informe_id && hasPermission('informes_ot.generate'), disabled: (row) => row.estado !== 'completado' },
    { icon: 'fa-file-pdf',       className: 'btn-ghost-success', title: 'Descargar informe firmado',      onClick: handleGenerarInforme, show: (row) => !!row.informe_id && (hasPermission('informes_ot.generate') || hasPermission('informes_ot.read')) },
    { icon: 'fa-edit',           className: 'btn-ghost-primary', title: 'Editar',                         onClick: handleEdit,                                                         show: () => hasPermission('ordenes_trabajo.update') },
    { icon: 'fa-trash',          className: 'btn-ghost-danger',  title: 'Eliminar',                       onClick: handleDelete,                                                       show: () => hasPermission('ordenes_trabajo.delete') },
  ];

  const otMobileCardRenderer = useCallback((row, index, visibleActions) => (
    <div key={row.id || index} className={`mobile-card ${isOrdenTrabajoVencida(row.fecha_ejecucion, row.estado) ? 'orden-trabajo-vencida' : ''}`.trim()}>
      <div className="mobile-card-header">
        <span className="mobile-card-title">
          <HighlightedText text={row.codigo_ot || ''} searchTerm={filters.search} />
        </span>
        <span className={`badge ${prioridadBadge[row.prioridad] || ''}`}>{row.prioridad}</span>
      </div>
      <div className="mobile-card-body">
        <div className="mobile-card-field">
          <span className="mobile-card-label">Equipo</span>
          <span className="mobile-card-value">
            {renderEquipoCell(row.equipo, filters.search)}
          </span>
        </div>
        <div className="mobile-card-field">
          <span className="mobile-card-label">Tipo</span>
          <span className="mobile-card-value">{row.tipo_mantenimiento?.nombre || '-'}</span>
        </div>
        <div className="mobile-card-field">
          <span className="mobile-card-label">Estado</span>
          <span className="mobile-card-value">
            <span className="status-dot-cell">
              <span className="status-dot" style={{ backgroundColor: dotColors[row.estado] }} />
              {row.estado?.replace('_', ' ')}
            </span>
          </span>
        </div>
        <div className="mobile-card-field">
          <span className="mobile-card-label">Asignado</span>
          <span className="mobile-card-value">
            {row.usuario_asignado?.nombre
              ? <HighlightedText text={row.usuario_asignado.nombre} searchTerm={filters.search} />
              : <span className="text-muted">Sin asignar</span>}
          </span>
        </div>
        <div className="mobile-card-field">
          <span className="mobile-card-label">Fecha</span>
          <span className="mobile-card-value">
            {row.fecha_ejecucion ? new Date(row.fecha_ejecucion).toLocaleDateString('es-CL') : '-'}
          </span>
        </div>
      </div>
      {visibleActions?.length > 0 && (
        <div className="card-actions">
          {visibleActions.map(action => renderActionButton(action, row, action.className?.includes('danger') ? 'btn-table-action' : 'btn-sm'))}
        </div>
      )}
    </div>
  ), [filters.search]);

  // Fecha mínima para creación
  const today = getDateKey(new Date());

  const createFields = useMemo(() => [
    {
      name: 'equipo_id',
      label: 'Equipo',
      type: 'select',
      required: true,
      options: equipos.map(e => ({ value: e.id, label: `${e.codigo} - ${e.marca} ${e.modelo}` }))
    },
    {
      name: 'plantilla_mantenimiento_id',
      label: 'Plantilla',
      type: 'select',
      required: true,
      options: plantillasList.filter(p => p.cantidad_actividades > 0).map(p => ({ value: p.id, label: p.nombre }))
    },
    { name: 'descripcion', label: 'Descripcion', type: 'textarea', required: true },
    {
      name: 'fecha_programada',
      label: 'Fecha Programada',
      type: 'date',
      required: true,
      width: 'half',
      min: today,
      validators: [(value) => validateDate(value, 'Fecha programada')]
    },
    {
      name: 'prioridad',
      label: 'Prioridad',
      type: 'select',
      required: true,
      width: 'half',
      options: [
        { value: 'baja', label: 'Baja' },
        { value: 'media', label: 'Media' },
        { value: 'alta', label: 'Alta' },
        { value: 'critica', label: 'Critica' }
      ]
    },
    {
      name: 'usuario_asignado_id',
      label: 'Asignar a Usuario',
      type: 'select',
      width: 'half',
      options: [{ value: '', label: 'Sin asignar' }, ...usuarios.map(u => ({ value: u.id, label: `${u.nombre} (${u.email})` }))]
    }
  ], [equipos, plantillasList, usuarios, today]);

  const asignacionBloqueada = ['en_progreso', 'completado', 'cancelado'].includes(editingOrdenTrabajo?.estado);

  const editFields = useMemo(() => [
    { name: 'descripcion', label: 'Descripcion', type: 'textarea', required: true },
    {
      name: 'prioridad',
      label: 'Prioridad',
      type: 'select',
      required: true,
      width: 'half',
      options: [
        { value: 'baja', label: 'Baja' },
        { value: 'media', label: 'Media' },
        { value: 'alta', label: 'Alta' },
        { value: 'critica', label: 'Critica' }
      ]
    },
    {
      name: 'usuario_asignado_id',
      label: 'Asignar a Usuario',
      type: 'select',
      width: 'half',
      disabled: asignacionBloqueada,
      options: [{ value: '', label: 'Sin asignar' }, ...usuarios.map(u => ({ value: u.id, label: `${u.nombre} (${u.email})` }))]
    }
  ], [usuarios, asignacionBloqueada]);

  /**
   * Wrapper de handleSave que:
   * - En creación: agrega tipo_mantenimiento_id (correctivo) y estado (programado)
   * - En edición: preserva campos FK no editables
   * @param {Object} formData - Datos del formulario
   * @returns {Promise<void>}
   */
  const handleSaveOrdenTrabajo = useCallback(async (formData) => {
    // Limpiar FKs opcionales vacías ('' → MySQL coerce a 0 → FK falla)
    const cleanData = { ...formData };
    if (cleanData.usuario_asignado_id === '') cleanData.usuario_asignado_id = null;
    if (cleanData.plantilla_mantenimiento_id === '') cleanData.plantilla_mantenimiento_id = null;

    if (!editingOrdenTrabajo) {
      // MODO CREACIÓN
      const tipoCorrectivo = tipos.find(t => t.codigo === 'CORR');

      if (!tipoCorrectivo) {
        showToast('Error: No se encontró el tipo de mantenimiento "correctivo". Recargue la página.', 'error');
        closeModal();
        return;
      }

      const dataConDefaults = {
        ...cleanData,
        tipo_mantenimiento_id: tipoCorrectivo.id,
        estado: 'programado'
      };

      await handleSave(dataConDefaults);
    } else {
      // MODO EDICIÓN: Preservar campos FK y no editables
      const dataConFKs = {
        ...cleanData,
        equipo_id: editingOrdenTrabajo.equipo_id,
        tipo_mantenimiento_id: editingOrdenTrabajo.tipo_mantenimiento_id,
        plantilla_mantenimiento_id: editingOrdenTrabajo.plantilla_mantenimiento_id,
        fecha_programada: editingOrdenTrabajo.fecha_programada,
        fecha_ejecucion: editingOrdenTrabajo.fecha_ejecucion,
        estado: editingOrdenTrabajo.estado,
        programa_mantenimiento_id: editingOrdenTrabajo.programa_mantenimiento_id
      };

      await handleSave(dataConFKs);
    }
  }, [editingOrdenTrabajo, tipos, handleSave, showToast, closeModal]);

  /**
   * Wrapper de handleCreate que verifica que los catálogos estén cargados
   * antes de abrir el modal de creación
   * @returns {void}
   */
  const handleCreateWithCheck = useCallback(() => {
    if (equipos.length === 0 || tipos.length === 0) {
      showToast('Cargando catálogos, espere un momento...', 'warning');
      return;
    }
    handleCreate();
  }, [equipos.length, tipos.length, handleCreate, showToast]);

  const filterConfig = useMemo(() => [
    {
      name: 'search',
      type: 'text',
      placeholder: 'Buscar por equipo, descripcion...',
      icon: 'fa-search'
    },
    {
      name: 'estado',
      type: 'select',
      label: 'Estado',
      options: [
        { value: '', label: 'Todos' },
        { value: 'programado', label: 'Programado' },
        { value: 'en_progreso', label: 'En progreso' },
        { value: 'completado', label: 'Completado' },
        { value: 'cancelado', label: 'Cancelado' }
      ]
    },
    {
      name: 'prioridad',
      type: 'select',
      label: 'Prioridad',
      options: [
        { value: '', label: 'Todas' },
        { value: 'critica', label: 'Crítica' },
        { value: 'alta', label: 'Alta' },
        { value: 'media', label: 'Media' },
        { value: 'baja', label: 'Baja' }
      ]
    },
    {
      name: 'tipo_mantenimiento_id',
      type: 'select',
      label: 'Tipo',
      options: [
        { value: '', label: 'Todos' },
        ...tipos.map(t => ({ value: String(t.id), label: t.nombre }))
      ]
    }
  ], [tipos]);

  return (
    <>
      <Navbar />
      <div className="main-content">
        <Breadcrumb items={[
          { label: 'Inicio', to: ROUTES.HOME },
          { label: 'Órdenes de Trabajo' }
        ]} />

        <FilterPanel
          filters={filters}
          onFilterChange={handleFilterChange}
          filterConfig={filterConfig}
          actionConfig={[
            { label: 'Exportar Excel', icon: 'fa-file-excel', variant: 'secondary', onClick: exportToExcel },
            { label: 'Nueva Orden',    icon: 'fa-plus',       variant: 'primary',   onClick: handleCreateWithCheck, show: () => hasPermission('ordenes_trabajo.create') }
          ]}
        />

        <EntityTable
          columns={columns}
          data={ordenesTrabajo}
          loading={loading}
          searchTerm={filters.search}
          emptyMessage="No se encontraron órdenes de trabajo"
          getRowClassName={(row) => isOrdenTrabajoVencida(row.fecha_ejecucion, row.estado) ? 'orden-trabajo-vencida' : ''}
          animateRows={true}
          actions={otActions}
          mobileCardRenderer={otMobileCardRenderer}
          onSort={handleSort}
          sortField={sortField}
          sortOrder={sortOrder}
        />
        <Pagination
          currentPage={pagination.page}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleLimitChange}
          loading={loading}
        />

        <EntityModal
          isOpen={modalOpen}
          onClose={closeModal}
          onSave={handleSaveOrdenTrabajo}
          title={editingOrdenTrabajo ? 'Editar Orden de Trabajo' : 'Nueva Orden de Trabajo'}
          fields={editingOrdenTrabajo ? editFields : createFields}
          initialData={editingOrdenTrabajo}
        />

        {/* Modal de Programar */}
        <EntityModal
          isOpen={programarDialog.open}
          onClose={() => setProgramarDialog({ open: false, item: null })}
          onSave={handleProgramar}
          title="Programar Orden de Trabajo"
          fields={[
            { name: 'fecha_ejecucion', label: 'Fecha de Ejecución', type: 'date', required: true, min: today },
            {
              name: 'motivo_reprogramacion_id',
              label: 'Motivo',
              type: 'select',
              required: false,
              options: motivosReprogramacion.map(m => ({ value: m.id, label: m.nombre }))
            },
            { name: 'motivo', label: 'Observaciones', type: 'textarea', required: false }
          ]}
          initialData={programarDialog.item ? {
            fecha_ejecucion: programarDialog.item.fecha_ejecucion
          } : null}
          crossFieldValidator={(formData) => {
            const errors = {};
            if (programarDialog.item && programarDialog.item.programaciones_count > 0 && !formData.motivo_reprogramacion_id) {
              errors.motivo_reprogramacion_id = 'El motivo es requerido en reprogramaciones';
            }
            return errors;
          }}
        />

        <HistorialProgramacionModal
          open={historialDialog.open}
          data={historialDialog.data}
          item={historialDialog.item}
          onClose={() => setHistorialDialog({ open: false, data: [], item: null })}
        />

        <ConfirmDialog
          isOpen={confirmDialog.open}
          onClose={closeConfirm}
          onConfirm={confirmDelete}
          title="Eliminar Orden de Trabajo"
          message="¿Esta seguro de eliminar esta orden de trabajo?"
          confirmText="Eliminar"
          variant="danger"
        />
      </div>

      <GenerarInformeModal
        open={informeDialog.open}
        item={informeDialog.item}
        onConfirm={handleConfirmarInforme}
        onClose={() => setInformeDialog({ open: false, item: null })}
        loading={generandoInforme}
      />
    </>
  );
}

export default OrdenesTrabajo;
